/**
 * Utility functions for booking operations
 */

import type { Booking } from '../types';
import { MONITORED_AIRLINES } from '../constants';

/**
 * Whether a booking can be price-checked.
 * Supported airlines are monitorable for one-way, roundtrip, and multi-city.
 */
export function isMonitorable(booking: Booking): boolean {
  const isSupported = MONITORED_AIRLINES.includes(booking.airline_code);
  if (!isSupported) return false;
  return true;
}

/**
 * Generate rebooking URL based on airline code and booking details.
 * Supports both one-way and roundtrip URLs.
 */
export function getRebookingUrl(
  airlineCode: string,
  origin: string,
  destination: string,
  departureDate: string,
  isAwardBooking: boolean,
  returnDate?: string,
): string {
  // Format date as YYYY-MM-DD for URL params
  const date = new Date(departureDate);
  const formattedDate = date.toISOString().split('T')[0];
  const formattedReturnDate = returnDate ? new Date(returnDate).toISOString().split('T')[0] : undefined;
  const isRoundtrip = !!formattedReturnDate;

  // Common airline booking URLs
  type UrlGen = (origin: string, dest: string, date: string, isAward: boolean, retDate?: string) => string;
  const airlineUrls: Record<string, UrlGen> = {
    // Delta
    'DL': (o, d, dt, award, rd) => {
      const tripType = rd ? 'ROUND_TRIP' : 'ONE_WAY';
      const base = `https://www.delta.com/flight-search/book-a-flight?origin=${o}&destination=${d}&departureDate=${dt}&tripType=${tripType}&paxCount=1`;
      const ret = rd ? `&returnDate=${rd}` : '';
      return award ? `${base}${ret}&fareClass=AWARD` : `${base}${ret}`;
    },

    // American Airlines
    'AA': (o, d, dt, award, rd) => {
      const tripType = rd ? 'roundTrip' : 'oneWay';
      const base = `https://www.aa.com/booking/find-flights?from=${o}&to=${d}&departDate=${dt}&passengers=1&tripType=${tripType}`;
      const ret = rd ? `&returnDate=${rd}` : '';
      return award ? `${base}${ret}&bookingType=award` : `${base}${ret}`;
    },

    // United Airlines
    'UA': (o, d, dt, award, rd) => {
      const tt = rd ? '1' : '3'; // United uses tt=1 for roundtrip, tt=3 for one-way
      const base = `https://www.united.com/en/us/fsr/choose-flights?f=${o}&t=${d}&d=${dt}&tt=${tt}&at=1&sc=7&px=1&taxng=1&newHP=True&clm=7&st=bestmatches`;
      const ret = rd ? `&r=${rd}` : '';
      return award ? `${base}${ret}&fareType=award` : `${base}${ret}`;
    },
  };

  // Get airline-specific URL or fallback to Google Flights
  const urlGenerator = airlineUrls[airlineCode.toUpperCase()];

  if (urlGenerator) {
    return urlGenerator(origin, destination, formattedDate, isAwardBooking, isRoundtrip ? formattedReturnDate : undefined);
  }

  // Fallback to Google Flights
  const rtParam = formattedReturnDate ? `%20return%20${formattedReturnDate}` : '';
  return `https://www.google.com/travel/flights?q=flights%20from%20${origin}%20to%20${destination}%20on%20${formattedDate}${rtParam}`;
}

/**
 * Check if a flight has departed. Uses last segment's arrival + 24h buffer
 * so bookings aren't auto-archived while passengers might still be mid-trip
 * (e.g. delayed flights, missing connection data).
 */
export function isFlightPast(segments: Array<{ arrival_datetime: string }> | undefined): boolean {
  if (!segments || segments.length === 0) return false;

  // Get the last segment (final arrival)
  const lastSegment = segments[segments.length - 1];
  const arrivalTime = new Date(lastSegment.arrival_datetime);

  // Add a small buffer (flight is "past" 24 hours after arrival)
  const pastThreshold = new Date(arrivalTime.getTime() + 24 * 60 * 60 * 1000);

  return new Date() > pastThreshold;
}

/**
 * Get time until flight departure
 */
export function getTimeUntilFlight(departureDateTime: string): {
  isPast: boolean;
  text: string;
  urgency: 'past' | 'urgent' | 'soon' | 'future';
} {
  const departure = new Date(departureDateTime);
  const now = new Date();
  const diffMs = departure.getTime() - now.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

  if (diffMs < 0) {
    return { isPast: true, text: 'Departed', urgency: 'past' };
  }

  if (diffHours < 24) {
    return { isPast: false, text: `In ${diffHours}h`, urgency: 'urgent' };
  }

  if (diffDays <= 7) {
    return { isPast: false, text: `In ${diffDays} days`, urgency: 'soon' };
  }

  return { isPast: false, text: `In ${diffDays} days`, urgency: 'future' };
}
